import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Building2 } from 'lucide-react';
import { ChevronDown, ChevronRight, ChevronUp } from './icons';

import { EMPRESAS_PRINCIPALES as PRINCIPALES, EMPRESAS_OTROS as OTROS, normalizarEmpresa } from '../utils/empresaUtils';

const EmpresaSelector = ({ value: valorCrudo, onChange, placeholder = 'Selecciona una empresa', required = false, className = '' }) => {
    const value = normalizarEmpresa(valorCrudo);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [otrosExpanded, setOtrosExpanded] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);

    const updateDropdownPosition = () => {
        if (isDropdownOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setDropdownPosition({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX, width: rect.width });
        }
    };

    useEffect(() => {
        if (!isDropdownOpen) return;
        updateDropdownPosition();
        const onScroll = () => updateDropdownPosition();
        const onResize = () => updateDropdownPosition();
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onResize);
        };
    }, [isDropdownOpen]);

    useEffect(() => {
        if (!isDropdownOpen) return;
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
                buttonRef.current && !buttonRef.current.contains(e.target)) {
                setIsDropdownOpen(false);
                setOtrosExpanded(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isDropdownOpen]);

    const selectEmpresa = (empresa) => {
        if (!empresa) return;
        onChange(empresa);
        setIsDropdownOpen(false);
    };

    return (
        <div className={`relative ${className}`}>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Empresa {required && <span className="text-red-500">*</span>}
            </label>

            <div className="relative" style={{ zIndex: isDropdownOpen ? 9999 : 'auto' }}>
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building2 className="h-4 w-4 text-gray-400" />
                </div>
                <button
                    ref={buttonRef}
                    type="button"
                    onClick={() => { setIsDropdownOpen(!isDropdownOpen); if (OTROS.includes(value)) setOtrosExpanded(true); }}
                    className="w-full pl-10 pr-10 py-2.5 text-left border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                >
                    <span className={value ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}>
                        {value || placeholder}
                    </span>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                        {isDropdownOpen ? (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                        ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                        )}
                    </div>
                </button>
            </div>

            {isDropdownOpen && createPortal(
                <div
                    ref={dropdownRef}
                    className="fixed z-[9999] bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-80 overflow-y-auto"
                    style={{ top: dropdownPosition.top, left: dropdownPosition.left, width: dropdownPosition.width, minWidth: '300px' }}
                >
                    <div className="p-2">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 px-2">Empresas</div>
                        {PRINCIPALES.map((e) => (
                            <button
                                key={e}
                                type="button"
                                onClick={() => selectEmpresa(e)}
                                className={`w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-600 border-b border-gray-100 dark:border-gray-600 ${value === e ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-white'}`}
                            >
                                <div className="flex items-center">
                                    <Building2 className="w-4 h-4 mr-2 text-gray-400" />
                                    <span className="text-sm">{e}</span>
                                </div>
                            </button>
                        ))}
                    </div>

                    <div className="border-t border-gray-200 dark:border-gray-600">
                        <div className="p-2">
                            <button
                                type="button"
                                onClick={() => setOtrosExpanded(!otrosExpanded)}
                                className="flex items-center justify-between w-full text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 px-2 hover:bg-gray-50 dark:hover:bg-gray-600 rounded py-1"
                            >
                                <span>Otros</span>
                                {otrosExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                            {otrosExpanded && OTROS.map((e) => (
                                <button
                                    key={e}
                                    type="button"
                                    onClick={() => selectEmpresa(e)}
                                    className={`w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-600 border-b border-gray-100 dark:border-gray-600 last:border-b-0 ${value === e ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-white'}`}
                                >
                                    <div className="flex items-center">
                                        <Building2 className="w-4 h-4 mr-2 text-gray-400" />
                                        <span className="text-sm">{e}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default EmpresaSelector;
